import { forwardRef, MouseEvent, useState } from "react";
import {
  Select as MUISelect,
  FormControl,
  MenuItem,
  Chip,
  SelectChangeEvent,
  Box,
  Typography,
  InputLabel,
} from "@mui/material";
import { MultiSelectProps } from "./MultiSelect.props";
import { useTranslation } from "react-i18next";

const MultiSelect = forwardRef<HTMLSelectElement, MultiSelectProps>(
  ({ options, items, fieldError, disableItem, onSelect, ...rest }, ref) => {
    // local state
    const [selectedItems, setSelectedItems] = useState<number[]>(items);
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    // destructures
    const { label } = rest;

    // handlers
    const handleMouseDown = (event: MouseEvent) => event.stopPropagation();
    const handleChange = (e: SelectChangeEvent<unknown>) => {
      const values = e.target.value as number[];
      setSelectedItems(values);
      onSelect(values);
    };
    const handleDeleteItem = (id: number) => () => {
      setSelectedItems(selectedItems.filter((item) => item !== id));
      onSelect(selectedItems.filter((item) => item !== id));
    };

    return (
      <FormControl fullWidth size="small">
        <InputLabel id="demo-simple-select-label">{label}</InputLabel>
        <MUISelect
          multiple
          id={`${label}-multi-select`}
          labelId={`${label}-label`}
          value={selectedItems}
          defaultValue={selectedItems}
          onChange={handleChange}
          inputRef={ref}
          MenuProps={{
            keepMounted: true,
            disablePortal: true,
            PaperProps: {
              sx: {
                maxHeight: "500px",
                "& .MuiMenuItem-root": {
                  "&.Mui-selected": {
                    backgroundColor: "#E0E0E0",
                  },
                },
                "@media (max-width: 600px)": {
                  maxHeight: "200px",
                },
              },
            },
          }}
          open={isOpen}
          onClick={() => {
            setIsOpen(!isOpen);
          }}
          renderValue={(selected) => {
            const selectedItems = selected as number[];
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selectedItems.map((value) => {
                  return (
                    <Chip
                      key={value}
                      label={
                        options?.find(({ id }) => id === value)?.name || ""
                      }
                      onDelete={handleDeleteItem(value)}
                      onMouseDown={handleMouseDown}
                    />
                  );
                })}
              </Box>
            );
          }}
          {...rest}
        >
          {options?.map(({ id, name }) => (
            <MenuItem
              key={id}
              value={id}
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {name}
            </MenuItem>
          ))}
          <div
            style={{
              position: "sticky",
              bottom: 0,
              background: "white",
              zIndex: 1,
              padding: "8px",
              textAlign: "center",
            }}
            className="lg:hidden block"
          >
            <button
              style={{
                padding: "8px 16px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                width: "100%",
                borderRadius: "4px",
              }}
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            >
              {t("action.ok")}
            </button>
          </div>
        </MUISelect>
        {fieldError && (
          <Typography sx={{ color: "red" }} variant="caption" display="block">
            {fieldError?.message}
          </Typography>
        )}
      </FormControl>
    );
  }
);

MultiSelect.displayName = "MultiSelect";
export default MultiSelect;
